import type { ServiceBusHealth } from '../../../types/systemHealth'
import { cn } from '../../../shared/utils/cn'

interface DualWritePanelProps {
  data: ServiceBusHealth | undefined
  isLoading: boolean
}

const formatUpdated = (iso: string | null): string | null => {
  if (!iso) return null
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/**
 * The dual-write gate has two independent halves and both must be on. Showing
 * them separately is the point: "dual-write isn't running" is a very different
 * investigation depending on whether the app setting or the feature flag is off.
 */
export function DualWritePanel({ data, isLoading }: DualWritePanelProps) {
  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Dual-write</h2>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data ? (
        <p className="text-sm text-gray-500">No data.</p>
      ) : (
        <>
          <div className="space-y-3">
            <GateRow
              label="App setting"
              detail={
                data.dualWrite.envEnabled === null
                  ? 'SERVICE_BUS_DUAL_WRITE_ENABLED — readable only from the processor, which is unreachable'
                  : 'SERVICE_BUS_DUAL_WRITE_ENABLED on the function app'
              }
              enabled={data.dualWrite.envEnabled}
            />
            <GateRow
              label="Feature flag"
              detail={
                formatUpdated(data.dualWrite.flagUpdated)
                  ? `service_bus_dual_write · updated ${formatUpdated(data.dualWrite.flagUpdated)}`
                  : 'service_bus_dual_write'
              }
              enabled={data.dualWrite.flagEnabled}
            />
            <GateRow
              label="Effective"
              detail="Both halves must be on before the callback writes to Service Bus"
              enabled={data.dualWrite.effective}
              emphasis
            />
          </div>

          <p className="text-xs text-gray-400 mt-4">
            Read-only. Change via the function app setting and the{' '}
            <span className="font-mono">service_bus_dual_write</span> flag.
          </p>
        </>
      )}
    </section>
  )
}

/** null renders as "Unknown" — never as "Off", which would be a claim we cannot make. */
function GateRow({
  label,
  detail,
  enabled,
  emphasis,
}: {
  label: string
  detail: string
  enabled: boolean | null
  emphasis?: boolean
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          'mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0',
          enabled === null ? 'bg-gray-300 ring-2 ring-gray-200' : enabled ? 'bg-green-500' : 'bg-gray-300'
        )}
      />
      <div>
        <p className={cn('text-sm text-gray-900', emphasis && 'font-medium')}>
          {label}: {enabled === null ? 'Unknown' : enabled ? 'On' : 'Off'}
        </p>
        <p className="text-xs text-gray-400">{detail}</p>
      </div>
    </div>
  )
}
